/**
 * HeatmapPanel — cross-framework heatmap across all client engagements.
 *
 * Rows = clients, columns = 12 sub-brand frameworks, cells = overall
 * weighted score of the most recent run. Sparse cells render as empty
 * so coverage gaps are visually obvious.
 */
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const colorFor = (overall) => {
  if (overall == null) return "ek-surface-2 ek-text-muted";
  if (overall < 1.5) return "bg-red-50 text-red-900";
  if (overall < 2.5) return "bg-orange-50 text-orange-900";
  if (overall < 3.5) return "bg-amber-50 text-amber-900";
  if (overall < 4.25) return "bg-emerald-50 text-emerald-900";
  return "bg-emerald-100 text-emerald-900";
};

const HeatmapPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/assessments/heatmap");
      setData(data);
    } catch (e) {
      toast.error("Failed to load heatmap");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return <div className="ek-text-muted text-[14px]" data-testid="heatmap-loading">Loading…</div>;
  }
  if (!data || !data.columns || data.columns.length === 0) {
    return <div className="ek-text-muted text-[14px]">No frameworks configured yet.</div>;
  }

  return (
    <div className="space-y-6" data-testid="heatmap-panel">
      <div className="flex items-end justify-between">
        <div>
          <div className="ek-eyebrow text-[#7737F6] text-[11px]">Portfolio view</div>
          <h2 className="ek-heading text-2xl ek-text mt-1">Cross-framework heatmap</h2>
          <p className="text-[14px] ek-text-soft font-light mt-2 max-w-3xl">
            Latest overall maturity for every client × sub-brand. Each cell is the most-recent run&rsquo;s headline weighted score. Empty cells = no engagement run for that combo (yet). Capped cells are flagged with a small ⚠.
          </p>
        </div>
        <button type="button" onClick={reload} className="text-[#7737F6] hover:text-[#5D28C7] text-[12.5px] inline-flex items-center gap-1" data-testid="heatmap-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="border ek-line ek-surface overflow-x-auto">
        <table className="min-w-full text-[12.5px]">
          <thead>
            <tr className="ek-surface-2 border-b ek-line">
              <th className="px-3 py-2.5 text-left text-[10.5px] uppercase tracking-wider ek-text-muted sticky left-0 ek-surface-2 z-10 min-w-[200px]">Client</th>
              {data.columns.map((col) => (
                <th key={col.id} className="px-2 py-2.5 text-center text-[10.5px] uppercase tracking-wider ek-text-muted font-semibold" data-testid={`heatmap-col-${col.sub_brand?.toLowerCase()}`}>
                  {col.sub_brand}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr>
                <td colSpan={data.columns.length + 1} className="px-3 py-10 text-center ek-text-muted">
                  <div className="font-medium ek-text-soft">No engagements yet.</div>
                  <div className="mt-1 text-[12px]">When you create assessment runs the matrix populates one client per row.</div>
                </td>
              </tr>
            ) : data.rows.map((row, idx) => (
              <tr key={row.client} className="border-b ek-line last:border-b-0" data-testid={`heatmap-row-${idx}`}>
                <td className="px-3 py-2 sticky left-0 ek-surface z-10 font-medium ek-text border-r ek-line">{row.client}</td>
                {data.columns.map((col) => {
                  const cell = row.cells[col.id];
                  return (
                    <td key={col.id} className={`px-1.5 py-1.5 text-center align-middle ${colorFor(cell?.overall)}`} title={cell ? `${col.sub_brand} · ${cell.overall?.toFixed?.(2) ?? "—"} · ${cell.level ?? ""} · ${cell.completeness ?? 0}% completeness${cell.capped ? " · CAPPED" : ""}` : "No run"} data-testid={`heatmap-cell-${idx}-${col.sub_brand?.toLowerCase()}`}>
                      {cell?.overall != null ? (
                        <div className="flex flex-col items-center justify-center">
                          <div className="font-semibold text-[13px] tabular-nums">{cell.overall.toFixed(1)}</div>
                          {cell.capped ? <AlertTriangle size={9} className="mt-0.5 text-amber-700" /> : null}
                        </div>
                      ) : (
                        <span className="ek-text-muted">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] ek-text-soft">
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-red-50 border border-red-200" /> &lt; 1.5 Initial</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-orange-50 border border-orange-200" /> 1.5–2.5 Managed</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-amber-50 border border-amber-200" /> 2.5–3.5 Proactive</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-emerald-50 border border-emerald-200" /> 3.5–4.25 Optimised</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-emerald-100 border border-emerald-300" /> ≥ 4.25 Leading</div>
        <div className="flex items-center gap-1.5"><AlertTriangle size={10} className="text-amber-700" /> dealbreaker cap applied</div>
      </div>

      <div className="text-[11.5px] ek-text-muted">
        {data.client_count} client{data.client_count === 1 ? "" : "s"} · {data.coverage} coverage cells
      </div>
    </div>
  );
};

export default HeatmapPanel;
